import type { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { Sites } from '@ps/platform-message-framework/common/Sites';
import { UILayout } from '@ps/web-core-enums';
import { merge } from 'lodash';
import type { DeepPartial } from 'redux';

import type { Model, RootState, Slicer } from '../../types';
import type { CashierStorage, CesFeedBackForm, OnboardingCashier } from '../../types/Cashier';
import type { Notification } from '../../types/Notification';
import type { HGLocalStorage, PokerStorage } from '../../types/Poker';

export interface LocalSettingsState {
  cashier: CashierStorage;
  disableWithdrawFeePrompt?: boolean;
  documentsSubmitted?: boolean;
  emailVerificationDateTime?: string;
  lastLoginAcknowledged?: number;
  layoutMode?: string;
  lowBalancePrompt?: number;
  playingInsights?: number;
  playingInsightsFeedback?: number;
  playSessionLimitGR?: Notification;
  redirectTo?: string;
  registrationBrand?: BrandTypes;
  remainingInactivitySeconds?: number;
  restrictPlayingTime?: Notification | null;
  userSeenPrompt?:
    | Record<'firstDeposit' | 'firstWithdraw' | 'cashier', boolean>
    | Record<string, never>;
  sessionSummary?: Notification | null;
  showBalance?: boolean;
  validateAccountDays?: number | null;
  homegames?: HGLocalStorage;
  poker?: PokerStorage;
  cashierTour?: OnboardingCashier;
  clientVersion?: string;
  requestBrand?: BrandTypes;
  requestSite?: Sites;
}

export interface LocalSettingsModel extends Model<LocalSettingsState> {
  reducers: typeof reducers;
  selectors: typeof selectors;
}

const defaultCesFeedBackForm: CesFeedBackForm = {
  convertCurrencyExpireDate: 0,
  depositExpireDate: 0,
  p2pTransferExpireDate: 0,
  transactionHistoryExpireDate: 0,
  withdrawExpireDate: 0
};

const defaultCashier: CashierStorage = {
  cesFeedbackForm: defaultCesFeedBackForm
};

const defaultState: LocalSettingsState = {
  cashier: defaultCashier,
  documentsSubmitted: false,
  layoutMode: UILayout.Table,
  showBalance: true,
  homegames: { gameTileView: {} },
  poker: {
    spinAndGo: {
      onboardingFinished: false,
      onboardingModalShown: false
    }
  },
  cashierTour: {
    onboardingFinished: false,
    onboardingModalShown: false,
    lastModalShown: false
  }
};

const reducers = {
  'persist/REHYDRATE': (state: LocalSettingsState, { localSettings }: Partial<RootState> = {}) => ({
    ...state,
    ...localSettings
  }),
  set: (state: LocalSettingsState, payload: DeepPartial<LocalSettingsState> = {}) =>
    merge({}, state, payload)
};

const selectors = (slice: Slicer<LocalSettingsState>) => ({
  get() {
    return slice((state) => state || {});
  },
  getRegistrationBrand() {
    return slice((state) => state.registrationBrand as BrandTypes);
  },
  getCashier() {
    return slice((state) => state.cashier);
  }
});

const localSettings: LocalSettingsModel = {
  state: defaultState,
  reducers,
  selectors
};

export default localSettings;
